import { isEmpty, size } from 'es-toolkit/compat';
import React from 'react';

import { ResultsMode } from 'interfaces/experimentResults';
import { exportFeaturesForRuns } from 'services/export/exportFeaturesForRuns';
import { exportFeatures } from 'utils/exports';
import { useFormatBracketsOptions } from 'utils/formatBrackets/formatBracketsOptions';
import { useStudyStainTypeIds } from 'utils/queryHooks/stainType/useStudyStainTypes';
import { ExportOutputChoices, ExportScopeField, ExportScopeFields, ExportScopeValue } from './ExportFormFields';
import { ExportFormShell } from './ExportFormShell';
import {
  describeResultSource,
  excludedCasesOptions,
  getCasesFieldLabel,
  getScopeOptionLabels,
} from './exportScopeLabels';
import { SeparateOutputsStatus } from './SeparateOutputsStatus';
import { ResultsExportContext } from './useResultsExportContext';
import { useResultsExportUrlState } from 'services/export/useResultsExportUrlState';
import { StartExport, useSeparateOutputExports } from 'services/export/useSeparateOutputExports';

type FeaturesScope = 'selected' | 'filtered' | 'whole';

interface FeaturesExportFormProps {
  context: ResultsExportContext;
  skipPreloadingStudyStains?: boolean;
  startExport: StartExport;
  isExportRunning: boolean;
  onBack: () => void;
  backLabel?: string;
  downloadOnly?: boolean;
  // Replaces the read-only results source sentence when the host lets the user choose the source.
  resultSourceControl?: React.ReactNode;
  // Names the tab whose results source the export follows.
  resultSourceOwner?: string;
  // Why the export cannot start yet, when the host knows something the form does not.
  blockedReason?: string;
  // Several post-processing runs: one CSV per run in one ZIP. One run or none uses `context` as before.
  orchestrationIds?: string[];
}

export const FeaturesExportForm: React.FC<FeaturesExportFormProps> = ({
  context,
  skipPreloadingStudyStains,
  startExport,
  isExportRunning,
  onBack,
  backLabel,
  downloadOnly,
  resultSourceControl,
  resultSourceOwner = 'Dataset',
  blockedReason,
  orchestrationIds,
}) => {
  const { study, studyId, labId, resultsMode, orchestrationId, isSlidesMode } = context;
  const labels = getScopeOptionLabels(isSlidesMode);

  const scopeOptions: { value: FeaturesScope; label: string }[] = [
    ...(context.hasSpecificSelections ? [{ value: 'selected' as const, label: labels.selected }] : []),
    ...(context.areFiltersAppliedExceptStudy
      ? [
          {
            value: 'filtered' as const,
            label: `${labels.filtered}${context.hasSpecificSelections ? ' (including unselected)' : ''}`,
          },
        ]
      : []),
    { value: 'whole', label: labels.whole },
  ];

  const { query, update } = useResultsExportUrlState();
  // A stale URL value (for example a selection that no longer exists) falls back to the default scope.
  const scope =
    scopeOptions.find((option) => option.value === query.featuresExportScope)?.value ?? scopeOptions[0].value;
  const excludedCases = query.featuresExportExcludedCases === 'with' ? 'with' : 'without';
  const [includeFeatures, setIncludeFeatures] = React.useState(true);
  const [includeHighlighted, setIncludeHighlighted] = React.useState(false);

  const { data: stainTypesInStudy, isLoading: isLoadingStainTypes } = useStudyStainTypeIds(studyId, {
    enabled: Boolean(studyId) && !skipPreloadingStudyStains,
  });
  const formatBracketsOptions = useFormatBracketsOptions({ addStain: size(stainTypesInStudy) > 1, studyId });

  const hasHighlightedFeatures = !isEmpty(study?.highlightedFeatures);

  const casesParams =
    scope === 'selected'
      ? context.casesParamsWithoutPaginationAndSorting
      : scope === 'filtered'
      ? context.casesParamsWithoutSelections
      : undefined;

  const exportOptions = {
    studyId: studyId as string,
    context: {
      ...formatBracketsOptions,
      orchestrationId,
      skipPreloadingStudyStains: isLoadingStainTypes || skipPreloadingStudyStains,
      labId,
      studyId,
    },
    resultsMode,
    ...(resultsMode === ResultsMode.Manual && { orchestrationId }),
    hideExcluded: excludedCases === 'without',
    ...(casesParams && { casesParams }),
  };

  const exportRunIds = size(orchestrationIds) > 1 ? orchestrationIds : undefined;
  const exportOutput = (highlightedOnly: boolean) => (signal: AbortSignal) =>
    exportRunIds
      ? exportFeaturesForRuns({
          studyId: exportOptions.studyId,
          context: exportOptions.context,
          hideExcluded: exportOptions.hideExcluded,
          casesParams: exportOptions.casesParams,
          orchestrationIds: exportRunIds,
          highlightedOnly,
          signal,
        })
      : exportFeatures({ ...exportOptions, ...(highlightedOnly && { highlightedOnly }), signal });

  // With no highlighted features, Features is the only output, so it is fixed rather than a clearable choice.
  const featuresSelected = !hasHighlightedFeatures || includeFeatures;
  const outputs = [
    ...(featuresSelected
      ? [
          {
            id: 'features',
            title: 'Features',
            run: exportOutput(false),
          },
        ]
      : []),
    ...(includeHighlighted && hasHighlightedFeatures
      ? [
          {
            id: 'highlighted-features',
            title: 'Highlighted features',
            run: exportOutput(true),
          },
        ]
      : []),
  ];

  // One run downloads a CSV; several runs download one CSV per run in a ZIP.
  const fileKind = exportRunIds ? `.csv per run, in a .zip` : '.csv';
  const { outcomes, exportOutputs, retryFailedOutputs, hasRetryableOutputs } = useSeparateOutputExports(startExport);

  const guidance = blockedReason
    ? blockedReason
    : isEmpty(outputs)
    ? 'Select at least one output.'
    : formatBracketsOptions.isLoading
    ? 'Loading feature names…'
    : '';

  return (
    <ExportFormShell
      study={study}
      scope={
        <ExportScopeFields>
          <ExportScopeField
            id="features-cases"
            label={getCasesFieldLabel(isSlidesMode)}
            options={scopeOptions}
            value={scope}
            onChange={(value) => update({ featuresExportScope: value })}
          />
          {resultSourceControl ?? (
            <ExportScopeValue label="Results" note={`Follows the ${resultSourceOwner}.`}>
              {describeResultSource(resultsMode, orchestrationId)}
            </ExportScopeValue>
          )}
          <ExportScopeField
            id="features-excluded-cases"
            label="Excluded cases"
            options={excludedCasesOptions}
            value={excludedCases}
            onChange={(value) => update({ featuresExportExcludedCases: value === 'with' ? 'with' : undefined })}
          />
        </ExportScopeFields>
      }
      options={
        <ExportOutputChoices
          label="Output"
          outputs={[
            { id: 'features', label: `Features (${fileKind})`, checked: featuresSelected },
            ...(hasHighlightedFeatures
              ? [
                  {
                    id: 'highlighted-features',
                    label: `Highlighted features only (${fileKind})`,
                    checked: includeHighlighted,
                  },
                ]
              : []),
          ]}
          onChange={(id, checked) => (id === 'features' ? setIncludeFeatures(checked) : setIncludeHighlighted(checked))}
        />
      }
      status={
        <SeparateOutputsStatus
          outputs={outputs}
          outcomes={outcomes}
          canRetry={hasRetryableOutputs && !isExportRunning}
          onRetry={() => void retryFailedOutputs()}
        />
      }
      primaryLabel="Download"
      primaryDisabled={Boolean(guidance) || isExportRunning}
      primaryGuidance={guidance}
      onPrimary={() => void exportOutputs(outputs)}
      onBack={onBack}
      backLabel={backLabel}
      downloadOnly={downloadOnly}
    />
  );
};
